import {Injectable} from '@angular/core';
import {Observable, of, OperatorFunction, pipe} from 'rxjs';
import {StateService} from './state.service';
import {OcapiService} from '../ocapi/ocapi.service';
import {TvShopEntertainmentPack} from '../../model/tv-shop-entertainment-pack';
import {LoginService} from '../login/login.service';
import {catchError, map, mergeMap, tap} from 'rxjs/operators';
import {Route, Router} from '@angular/router';
import {OmapiIdsUtils} from '../../shared/utils/omapi-ids/omapi-ids-utils';
import {CustomerProductHoldingProduct, CustomerProductHoldingUtil} from '../../model/customer-product-holding/customer-product-holding.util';
import {UserUtil} from '../../model/customer/customer.util';
import {StreamingOption} from '../../model/sofy/sales-offer/option';
import {OcapiSynchronousService} from '../ocapi/ocapi.synchronous.service';
import {ActivateStreamingBundleResponse} from '../../interfaces/streaming-bundles/activate-streaming-bundle-response.interface';
import {Basket} from '../../interfaces/streaming-bundles/basket.interface';
import {EntertainmentPackService} from '../entertainment-pack/entertainment-pack-service';
import {TvShopEntertainmentPackGroupFactory} from '../../factories/tv-shop-entertainment-pack-group.factory';
import {TvShopEntertainmentPackGroups} from '../../constants/tv-shop-entertainment-pack/tv-shop-entertainment-pack-group.constants';
import {SalesOrder} from '../../interfaces/sales-order/sales-order.interface';
import {NetcrackerProduct} from '../../interfaces/products/netcracker-product.interface';
import {ErrorMessageConstants} from '../../shared/error-message-constants';

interface Loader {
  title: string;
  description: string;
  isLoading: boolean;
  loaderCount: number;
  openingTvShop: boolean;
}

interface Customer {
  currentUserType: string;
  isEligibleForStreamingBundles: boolean;
  isSohoCustomer?: boolean;
}

export interface TvShopState {
  loader: Loader;
  hasError: boolean;
  dtvIdentifier: string;
  customer: Customer;
  customerProductHolding: CustomerProductHoldingProduct[];
  isLoggedIn: boolean;
  isInitiated: boolean;
  isOrderProcessPending: boolean;
  isTrailerPlaying: boolean;
  basket: Basket;
  isStreamingBundleOrder: boolean;
  isStreamingServiceOrder: boolean;
  tvShopEntertainmentPacks: TvShopEntertainmentPack[];
  tvShopStreamingServices: TvShopEntertainmentPack[];
  tvShopEntertainmentPackGroups: TvShopEntertainmentPack[];
  salesOrder: SalesOrder;
}

const initialState: TvShopState = {
  loader: {
    title: undefined,
    description: 'Opening TV Shop ...',
    openingTvShop: true,
    isLoading: true,
    loaderCount: 1
  },
  hasError: false,
  customer: undefined,
  dtvIdentifier: undefined,
  tvShopEntertainmentPacks: [],
  tvShopStreamingServices: [],
  tvShopEntertainmentPackGroups: [],
  basket: {
    installationAddress: undefined,
    locationId: undefined,
    selectedTvShopEntertainmentPack: undefined,
    selectStreamingServiceOption: undefined
  },
  isStreamingBundleOrder: false,
  isStreamingServiceOrder: false,
  isLoggedIn: false,
  isInitiated: false,
  isOrderProcessPending: false,
  isTrailerPlaying: false,
  customerProductHolding: undefined,
  salesOrder: undefined
};

@Injectable({
  providedIn: 'root'
})
export class TvShopStateService extends StateService<TvShopState> {
  loader$: Observable<Loader> = this.select(state => state.loader);
  hasError$: Observable<boolean> = this.select(state => state.hasError);
  isInitiated$: Observable<boolean> = this.select(state => state.isInitiated);
  isOrderProcessPending$: Observable<boolean> = this.select(state => state.isOrderProcessPending);
  isTrailerPlaying$: Observable<boolean> = this.select(state => state.isTrailerPlaying);
  salesOrder$: Observable<SalesOrder> = this.select(state => state.salesOrder);

  constructor(
    private ocapiService: OcapiService,
    private loginService: LoginService,
    private entertainmentPackService: EntertainmentPackService,
    private ocapiSynchronousService: OcapiSynchronousService,
    private router: Router
  ) {
    super(initialState);
  }

  activateStreamingService(): Observable<ActivateStreamingBundleResponse> {
    const basket: Basket = this.state.basket;
    return this.ocapiSynchronousService.activateStreamingService(basket).pipe(
      tap((salesOrder: SalesOrder) => {
        if (this.selectSnapshot(state => state.isStreamingBundleOrder)) {
          this.handleBundleResponse(basket, salesOrder);
        }
        if (this.selectSnapshot(state => state.isStreamingServiceOrder)) {
          this.handleStandaloneResponse(basket, salesOrder);
        }
      }),
      map((salesOrder: SalesOrder) => {
        return {
          id: salesOrder.id,
          success: true
        };
      }),
      catchError((error) => {
        if (error.status === 403 && error.error && error.error.code === ErrorMessageConstants.OCAPI_ERR_ODSO123) {
          return of({
            errorCode: ErrorMessageConstants.OCAPI_ERR_ODSO123,
            errorMessage: ErrorMessageConstants.STREAMING_BUNDLE_WAITING_ACTIVATION
          });
        } else {
          return of({
            errorCode: ErrorMessageConstants.ERROR_FETCHING_STREAMING_SERVICES,
            errorMessage: ErrorMessageConstants.STREAMING_BUNDLE_ACTIVATION_ERROR
          });
        }
      })
    );
  }

  handleBundleResponse(basket: Basket, salesOrder: SalesOrder) {
    this.setUnsubscribedForOtherPacksinGroup(basket);
    if (basket.selectedTvShopEntertainmentPack) {
      const streamingBundle = this.state.tvShopStreamingServices.find((streamingService) => {
        return streamingService.omapiId === basket.selectedTvShopEntertainmentPack.omapiId;
      });
      if (streamingBundle) {
        this.setGroupPackToSubscribed(basket);
        streamingBundle.subscribed = true;
      }
    }
    if (basket.selectStreamingServiceOption) {
      const streamingBundleAddon = this.state.tvShopStreamingServices.find((streamingService) => {
        return streamingService.omapiId === basket.selectStreamingServiceOption.omapiid;
      });
      if (streamingBundleAddon) {
        streamingBundleAddon.subscribed = true;
      }
    }
    this.setState({salesOrder: salesOrder});
  }

  handleStandaloneResponse(basket: Basket, salesOrder: SalesOrder) {
    if (basket.selectedTvShopEntertainmentPack) {
      const entertainmentPack = this.state.tvShopStreamingServices.find((streamingService) => {
        return streamingService.omapiId === basket.selectedTvShopEntertainmentPack.omapiId;
      });
      if (entertainmentPack) {
        entertainmentPack.subscribed = true;
      }
    }
    this.setState({salesOrder: salesOrder});
  }

  setGroupPackToSubscribed(basket) {
    this.state.tvShopEntertainmentPackGroups.map(packGroup => {
      if (packGroup.childPacks.some(packs => packs.omapiId === basket.selectedTvShopEntertainmentPack.omapiId)) {
        packGroup.subscribed = true;
        this.state.basket.selectedTvShopEntertainmentPack = packGroup;
      }
      return packGroup;
    });
  }

  setUnsubscribedForOtherPacksinGroup(basket) {
    if (basket.selectedTvShopEntertainmentPack.isStreamingStandaloneProduct) {
      const streamingBundleGroup = this.getPackGroupFromOmapiId(basket);
      streamingBundleGroup.childPacks.forEach(pack => {
        this.state.tvShopStreamingServices.map(packs => {
          if (pack.omapiId && packs.omapiId && pack.omapiId !== basket.selectedTvShopEntertainmentPack.omapiId) {
            packs.subscribed = false;
          }
          return packs;
        });
      });
    }
  }

  getPackGroupFromOmapiId(basket) {
    return this.state.tvShopEntertainmentPackGroups.find(packGroup => packGroup.childPacks.some(packs => {
      return packs.omapiId === basket.selectedTvShopEntertainmentPack.omapiId;
    }));
  }

  setIsStreamingBundleOrder(isStreamingBundleOrder: boolean) {
    this.setState({isStreamingBundleOrder: isStreamingBundleOrder});
  }

  setIsStreamingServiceOrder(isStreamingServiceOrder: boolean) {
    this.setState({isStreamingServiceOrder: isStreamingServiceOrder});
  }

  setTvShopEntertainmentPacks(tvShopEntertainmentPacks: TvShopEntertainmentPack[]) {
    this.setState({tvShopEntertainmentPacks: tvShopEntertainmentPacks});
  }

  setSelectedTvShopEntertainmentPack(tvShopEntertainmentPack: TvShopEntertainmentPack) {
    this.setState({
      basket: Object.assign({}, this.state.basket, {selectedTvShopEntertainmentPack: tvShopEntertainmentPack})
    });
  }

  setTvShopEntertainmentPack(tvShopEntertainmentPack: TvShopEntertainmentPack) {
    if (tvShopEntertainmentPack.omapiId === OmapiIdsUtils.NETFLIX_PREMIUM_ID) {
      const netflixStandardTvEntertainmentPack = this.state.tvShopStreamingServices.find(
        (service) => service.omapiId === OmapiIdsUtils.NETFLIX_STANDARD_ID
      );

      this.setState({
        basket: Object.assign({}, this.state.basket, {
          selectedTvShopEntertainmentPack: netflixStandardTvEntertainmentPack,
          selectStreamingServiceOption: tvShopEntertainmentPack
        })
      });
    } else {
      this.setState({
        basket: Object.assign({}, this.state.basket, {
          selectedTvShopEntertainmentPack: tvShopEntertainmentPack,
          selectStreamingServiceOption: null
        })
      });
    }
  }

  setSelectedStreamingBundleOption(streamingOption: StreamingOption | undefined) {
    this.setState({
      basket: Object.assign({}, this.state.basket, {selectStreamingServiceOption: streamingOption})
    });
  }

  setLoader(loader: Loader) {
    this.setState({loader: loader});
  }

  setHasError(hasError: boolean) {
    this.setState({hasError: hasError});
  }

  setIsInitiated(isInitiated: boolean) {
    this.setState({isInitiated: isInitiated});
  }

  setIsOrderProcessPending(isOrderProcessPending: boolean) {
    this.setState({isOrderProcessPending: isOrderProcessPending});
  }

  setIsTrailerPlaying(isTrailerPlaying: boolean) {
    this.setState({isTrailerPlaying: isTrailerPlaying});
  }

  handleError(message: string, error?: any) {
    this.setHasError(true);
    console.error(message, error);
  }

  setLoading(isLoading: boolean) {
    let partial = {
      isLoading: this.state.loader.loaderCount > 1,
      loaderCount: this.state.loader.loaderCount - 1
    };

    if (isLoading) {
      partial = {
        isLoading: true,
        loaderCount: this.state.loader.loaderCount + 1
      };
    }

    const loaderState = Object.assign({}, this.state.loader, partial);
    this.setState({loader: loaderState});
  }

  login(): Observable<boolean> {
    return this.loginService.login().pipe(
      tap((dtvIdentifier) => {
        this.setState(
          {
            dtvIdentifier: dtvIdentifier,
            customer: {
              currentUserType: OmapiIdsUtils.currentUserType,
              isEligibleForStreamingBundles: false
            }
          }
        );
      }),
      mergeMap(() => {
        return this.ocapiService.getCustomerProductHolding();
      }),
      map((rawCustomerProductHolding) => {
        const dtvIdentifier = this.state.dtvIdentifier;
        const customerProductHolding = rawCustomerProductHolding && rawCustomerProductHolding['customerproductholding'];
        const hasDTVAndInternet = CustomerProductHoldingUtil.hasDTVAndInternet(customerProductHolding);
        const isDwnCustomer = UserUtil.isDwnCustomer(this.state.customer.currentUserType);
        const isSoho = UserUtil.isSohoCustomer(OmapiIdsUtils.currentDarwinUserType);
        const installationAddress = this.ocapiService.getAddressFromCustomerProductHolding(rawCustomerProductHolding, dtvIdentifier);
        const locationId = this.ocapiService.getLocationIdFromCustomerProductHolding(rawCustomerProductHolding, dtvIdentifier);
        this.setState(
          {
            isLoggedIn: true,
            customerProductHolding: customerProductHolding,
            customer: {
              currentUserType: this.state.customer.currentUserType,
              isEligibleForStreamingBundles: isDwnCustomer && hasDTVAndInternet && !isSoho,
              isSohoCustomer: isSoho
            },
            basket: Object.assign({}, this.state.basket, {
                installationAddress: installationAddress,
                locationId: locationId
              }
            )
          }
        );
        return true;
      }),
      catchError((error) => {
          this.setLoading(false);
          this.handleError(error);
          return of(false);
        }
      )
    );
  }

  initialise(routes: Route[]): Observable<boolean> {
    const dtvIdentifier = this.state.dtvIdentifier;
    const isSoho = this.state.customer.isSohoCustomer;

    return this.entertainmentPackService.getEntertainmentPacksAndStreamingServices(dtvIdentifier, isSoho).pipe(
      tap((result) => {
        const customerProductHolding = this.state.customerProductHolding;
        const enrichedStreamingServices = this.entertainmentPackService.enrichStreamingServicesWithOmapi(result.streamingServicesOptions, customerProductHolding);
        this.setState({tvShopStreamingServices: enrichedStreamingServices});
        return result;
      }),
      mergeMap((result) => {
        return this.entertainmentPackService.enrichTvEntertainmentPacksWithOmapi(result.tvShopEntertainmentPacks);
      }),
      map((tvShopEntertainmentPacks: TvShopEntertainmentPack[]) => {
        this.router.resetConfig(routes);
        this.setState({tvShopEntertainmentPacks: tvShopEntertainmentPacks});
        return true;
      }),
      this.handleProductsWaitingForActivation(),
      catchError((error) => {
          this.setLoading(false);
          this.handleError(error);
          return of(false);
        }
      )
    );
  }

  handleProductsWaitingForActivation(): OperatorFunction<any, any> {
    const customerType = this.state.customer.currentUserType;
    if (UserUtil.isDwnCustomer(customerType)) {
      return pipe(
        mergeMap(() => {
          const locationId = this.state.basket.locationId;
          return this.ocapiSynchronousService.getProductsWaitingToGetActivatedForLocation(locationId);
        }),
        map((products: NetcrackerProduct[]) => {
          this.state.tvShopStreamingServices.forEach((streamingService) => {
            const streamingServiceWaitingToBeActivated = products.some((product) => {
              return (
                product.omapiId === streamingService.omapiId ||
                product.options.some((option) => option.omapiId === streamingService.omapiId)
              );
            });

            if (streamingServiceWaitingToBeActivated) {
              streamingService.subscribed = true;
            }
          });
          return true;
        }),
        tap(() => {
          const tvShopEntertainmentPacks = this.state.tvShopEntertainmentPacks;
          const tvShopStreamingServices = this.state.tvShopStreamingServices;
          if (!this.state.customer.isSohoCustomer && tvShopStreamingServices.length) {
            this.setState(
              {
                tvShopEntertainmentPackGroups: TvShopEntertainmentPackGroupFactory.build(TvShopEntertainmentPackGroups, [...tvShopEntertainmentPacks, ...tvShopStreamingServices])
              }
            );
          }
          this.setState(
            {
              isInitiated: true
            }
          );
        })
      );
    }
    return pipe(
      tap(() => {
        this.setState({isInitiated: true});
      })
    );
  }

  logState() {
    console.log('state', this.state);
  }
}
